import React, { ChangeEvent } from 'react';
import { Box } from '@material-ui/core';
import { Checkbox, DateTimePicker, Option, Select, TextArea, TextBlock, Tooltip } from 'neon-design-system';
import { ArticleChangeHookType, ArticleEditError } from '../../../shared/global-enums';
import { ArticleValidationError } from '../../../utils/validator.utils';
import { prepareStaticDataList } from '../../../utils/list-utils';
import { Channel, ChannelTypes, User } from '../../../shared/types';

export interface ArticleDetailsProps {
	selectedContributors: string[];
	detailSummary: string;
	publishDate?: Date;
	listUsers: User[];
	canPublish: boolean;
	errorList: ArticleValidationError[];
	openToAll: boolean;
	showWatermark: boolean;
	displayInChartSection: boolean;
	isPinned: boolean;
	isTimeless: boolean;
	userHasTableuAccess: boolean;
	selectedChannel?: Channel;
	preparedChannelsList: Channel[];
	selectedOffset: string;
	onChangeValue: (changeType: ArticleChangeHookType, value: any | any[]) => void;
}

const sortedOffsets: Option[] = [
	{ label: '3 days', value: '3d' },
	{ label: '1 week', value: '1w' },
	{ label: '1 month', value: '1m' }
];

const TIMELESS_TOOLTIP_TEXT = 'Timeless articles must only contain information that is not date/time sensitive and will be searched by Neon Intelligence regardless of their publish date';

export const ArticleDetails = (props: ArticleDetailsProps) => {
	const charsLimit = 250;
	const charsLeft = +((charsLimit - (props.detailSummary?.length ?? 0)).toFixed());
	const charsMessage = `${charsLeft} characters remaining`;

	const contributorsValue: Option[] = prepareStaticDataList((props.listUsers || []).filter((userOption: User) => {
		return (props.selectedContributors?.includes(userOption._id.toString()));
	}));

	const listUsers = prepareStaticDataList(props.listUsers);

	const listChannels = prepareStaticDataList(props.preparedChannelsList);
	const channelValue: Option | undefined = (listChannels || []).find(x => x.value === props.selectedChannel?._id);
	const sortedOffsetsValue: Option | undefined = (sortedOffsets).find(_ => _.value === props.selectedOffset);

	const showOpenToAllCheckbox = props.selectedChannel && props.selectedChannel.type !== ChannelTypes.freeOpen;
	const isPremiumChannel = props.selectedChannel && props.selectedChannel.type === ChannelTypes.premium;

	const contributorError = props.errorList.find(x => x.errorType === ArticleEditError.ContributorAbsent)?.message;
	const channelError = props.errorList.find(x => x.errorType === ArticleEditError.ChannelAbsent)?.message;
	const summaryError = props.errorList.find(x => x.errorType === ArticleEditError.SummaryAbsent)?.message;

	return (
		<Box display="flex" flexDirection="column">
			<Box>
				<Select
					id="channel-select"
					label="Channel"
					size="medium"
					clearable
					menuPlacement="bottom"
					placeholder="Select from the list..."
					error={!!channelError}
					errorText={channelError}
					value={channelValue}
					onChange={(opt: Option) => {
						props.onChangeValue(ArticleChangeHookType.DetailsChannel, opt?.value?.toString());
					}}
					options={listChannels}
				/>
			</Box>
			{
				showOpenToAllCheckbox && (
					<>
						<Box mt={2}>
							<Checkbox
								id="premium-checkbox"
								label={isPremiumChannel ? 'Free for all Insights users' : 'Open to all Insights users'}
								value="openToAll"
								checked={props.openToAll}
								onChange={({ target }) => {
									props.onChangeValue(ArticleChangeHookType.DetailsOpenToAll, target.checked)
								}}
							/>
						</Box>
						<Box mt={1} pr={6}>
							<TextBlock styleName="caption1" ink="muted">
								{
									isPremiumChannel ? 'Make the article free for everyone on Insights, even non-subscribers.' :
									'Make the article open and available to all users within Insights.'
								}
							</TextBlock>
						</Box>
						{
							!props.openToAll && (
								<Box mt={2}>
									<Select
										id="premium-offset-select-contributor"
										label="Expiry"
										size="medium"
										clearable
										required={false}
										menuPlacement="bottom"
										placeholder="Select from the list..."
										value={sortedOffsetsValue}
										onChange={(opt: Option) => {
											props.onChangeValue(ArticleChangeHookType.DetailsPremiumOffset, opt?.value?.toString());
										}}
										options={sortedOffsets}
									/>
									<Box mt={1} pr={6}>
										<TextBlock styleName="caption1" ink="muted">
											Starting from the selected day, the article will become free for everyone.
										</TextBlock>
									</Box>
								</Box>
							)
						}
					</>
				)
			}
			<Box mt={2}>
				<Select
					id="article-select-contributor"
					size="medium"
					clearable={true}
					menuPlacement="bottom"
					multi={true}
					label="Contributors"
					placeholder="Select from the list..."
					value={contributorsValue}
					error={!!contributorError}
					errorText={contributorError}
					onChange={(options: Option[]) => {
						props.onChangeValue(ArticleChangeHookType.DetailsContributor, (options ?? []).map(x => x.value) as string[]);
					}}
					options={listUsers}
				/>
			</Box>
			<Box mt={2}>
				<TextArea
					id="article-input-detail-summary"
					fullWidth
					type="text"
					label="Summary"
					value={props.detailSummary}
					rowsMax={3}
					error={!!summaryError}
					errorText={summaryError}
					showProgressBar={true}
					progressBarIndicator={props.detailSummary?.length ?? 0}
					progressBarLimit={charsLimit}
					progressBarHelperText={charsMessage}
					onChange={(e: ChangeEvent<any>) => {
						const val: string = e?.target?.value.slice(0, 250);
						props.onChangeValue(ArticleChangeHookType.DetailsSummary, val);
					}}
				/>
				<Box mt={1}>
					<TextBlock styleName="caption1">
						A short extract of the article will be shown in listing pages and the article page.
					</TextBlock>
				</Box>
			</Box>
			{props.canPublish &&
				<Box mt={2}>
					<Box mt={1}>
						<DateTimePicker
							id="article-date-time-picker"
							variant="dialog"
							disableFuture={true}
							label="Custom Publish date"
							optional
							invalidDateMessage="Invalid Publish Date"
							onChange={(date: Date | null) => {
								props.onChangeValue(ArticleChangeHookType.DetailsPublishDate, date);

							}}
							value={props.publishDate ? props.publishDate : null}
						/>
					</Box>
				</Box>
			}
			<Box mt={2}>
				<Checkbox
					id="watermark-checkbox"
					label="Show watermark"
					value="showWatermark"
					checked={props.showWatermark}
					onChange={({target}) => {
						props.onChangeValue(ArticleChangeHookType.DetailsShowWatermark, target.checked)
					}}
				/>
			</Box>
			<Box mt={2}>
				<Checkbox
					id="isPinned-checkbox"
					label="Pin article"
					value="isPinned"
					checked={props.isPinned}
					onChange={({target}) => {
						props.onChangeValue(ArticleChangeHookType.DetailsIsPinned, target.checked)
					}}
				/>
			</Box>
			{
				props.userHasTableuAccess ? (
					<Box mt={2}>
						<Checkbox
							id="display-in-chart-section-checkbox"
							label="Display in chart section"
							value="displayInChartSection"
							checked={props.displayInChartSection}
							onChange={({target}) => {
								props.onChangeValue(ArticleChangeHookType.DetailsDisplayInChartSection, target.checked)
							}}
						/>
					</Box>
				) : <></>
			}
			<Box mt={2}>
				<Tooltip title={TIMELESS_TOOLTIP_TEXT}>
					<Box>
						<Checkbox
							id="isTimeless-checkbox"
							label="Timeless article"
							value="isTimeless"
							checked={props.isTimeless}
							onChange={({target}) => {
								props.onChangeValue(ArticleChangeHookType.DetailsIsTimeless, target.checked)
							}}
						/>
					</Box>
				</Tooltip>
			</Box>
		</Box>
	);

};

export default ArticleDetails;
